import React, { useEffect, useState } from "react";
import { useTheme } from "next-themes";
import { Moon, Sun, Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";

const linksLeft = [
  { label: "Accueil", id: "hero" },
  { label: "Services", id: "services" },
  { label: "Portfolio", id: "portfolio" },
];
const linksRight = [
  { label: "Expertises", id: "realisations" },
  { label: "À propos", id: "about" },
  { label: "Contact", id: "contact" },
];

function NavLink({ id, label }) {
  const handle = (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  return (
    <a
      href={`#${id}`}
      onClick={handle}
      data-testid={`nav-link-${id}`}
      className="text-sm tracking-wide uppercase font-medium opacity-80 hover:opacity-100 transition-all duration-300 ease-in-out hover:tracking-widest"
    >
      {label}
    </a>
  );
}

export default function Navbar() {
  const { resolvedTheme, setTheme } = useTheme();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const isDark = resolvedTheme === "dark";

  return (
    <header
      data-testid="navbar"
      className={cn(
        "fixed top-0 inset-x-0 z-50 transition-all duration-500",
        isDark ? "glass-nav-dark" : "glass-nav-light",
        scrolled ? "py-2" : "py-3"
      )}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 flex items-center justify-between">
        {/* Left links - desktop */}
        <nav className="hidden md:flex items-center gap-8 flex-1">
          {linksLeft.map((l) => (
            <NavLink key={l.id} {...l} />
          ))}
        </nav>

        {/* Center Logo */}
        <a
          href="#hero"
          onClick={(e) => {
            e.preventDefault();
            window.scrollTo({ top: 0, behavior: "smooth" });
          }}
          data-testid="navbar-logo"
          className="flex-1 md:flex-none text-center select-none flex justify-center items-center"
        >
          <img
            src="https://customer-assets.emergentagent.com/job_brams-portfolio/artifacts/ssol55p1_Logo%20bramsgraphic%20white.png"
            alt="Brams Graphic Design"
            className={cn(
              "object-contain transition-all duration-500",
              scrolled ? "h-16 md:h-20" : "h-20 md:h-28",
              isDark ? "" : "invert"
            )}
          />
        </a>

        {/* Right links - desktop */}
        <nav className="hidden md:flex items-center gap-8 flex-1 justify-end">
          {linksRight.map((l) => (
            <NavLink key={l.id} {...l} />
          ))}
          <button
            data-testid="theme-toggle"
            onClick={() => setTheme(isDark ? "light" : "dark")}
            className={cn(
              "w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 ease-in-out hover:scale-110",
              isDark
                ? "bg-white/5 border border-white/10 hover:bg-white/10"
                : "bg-black/5 border border-black/10 hover:bg-black/10"
            )}
            aria-label="Toggle theme"
          >
            {isDark ? <Sun size={16} /> : <Moon size={16} />}
          </button>
        </nav>

        {/* Mobile button */}
        <button
          data-testid="mobile-menu-btn"
          className="md:hidden p-2"
          onClick={() => setOpen((v) => !v)}
          aria-label="Toggle menu"
        >
          {open ? <X size={20} /> : <Menu size={20} />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div
          data-testid="mobile-menu"
          className={cn(
            "md:hidden absolute inset-x-0 top-full border-t",
            isDark ? "bg-black/90 border-white/10" : "bg-white/95 border-black/10"
          )}
        >
          <div className="max-w-7xl mx-auto px-6 py-6 flex flex-col gap-5">
            {[...linksLeft, ...linksRight].map((l) => (
              <a
                key={l.id}
                href={`#${l.id}`}
                onClick={(e) => {
                  e.preventDefault();
                  setOpen(false);
                  document
                    .getElementById(l.id)
                    ?.scrollIntoView({ behavior: "smooth" });
                }}
                data-testid={`mobile-nav-link-${l.id}`}
                className="text-sm uppercase tracking-widest font-medium"
              >
                {l.label}
              </a>
            ))}
            <button
              data-testid="mobile-theme-toggle"
              onClick={() => setTheme(isDark ? "light" : "dark")}
              className="text-sm uppercase tracking-widest font-medium opacity-70 flex items-center gap-2"
            >
              {isDark ? <Sun size={14} /> : <Moon size={14} />}
              {isDark ? "Mode clair" : "Mode sombre"}
            </button>
          </div>
        </div>
      )}
    </header>
  );
}
